import { useEffect, useState } from 'react'
import { api } from '../../lib/api'

interface Profile {
  id: string
  name: string
  is_default: number
}

interface JobCreatorProps {
  siteId: string
  onClose: () => void
}

export function JobCreator({ siteId, onClose }: JobCreatorProps) {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [profileId, setProfileId] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get<{ data: Profile[] }>('/provisioning-profiles')
      .then(res => {
        setProfiles(res.data)
        const defaultProfile = res.data.find(p => p.is_default === 1)
        if (defaultProfile) setProfileId(defaultProfile.id)
        else if (res.data.length > 0) setProfileId(res.data[0].id)
      })
      .catch(() => setError('Failed to load profiles'))
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate() {
    if (!profileId) { setError('Select a profile'); return }
    setSaving(true)
    setError('')
    try {
      await api.post('/provisioning-jobs', { site_id: siteId, profile_id: profileId })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create job')
    } finally { setSaving(false) }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, fontWeight: 500 }}
        >
          &larr; Back
        </button>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>New Provisioning Job</h2>
      </div>

      <div style={{
        maxWidth: 440, padding: 20,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-xs)',
      }}>
        {error && (
          <div style={{
            padding: '8px 12px', fontSize: 13, color: 'var(--err)',
            background: 'rgba(229,62,62,0.06)', borderRadius: 6, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading profiles...</div>
        ) : profiles.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            No provisioning profiles found. Create one in Settings first.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>
                Profile
              </label>
              <select
                value={profileId}
                onChange={e => setProfileId(e.target.value)}
                style={{
                  width: '100%', padding: '7px 10px', fontSize: 13,
                  border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--bg)', color: 'var(--text)',
                }}
              >
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.is_default === 1 ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              Expected devices will be counted from the site documentation.
            </p>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '7px 14px', fontSize: 13, fontWeight: 500,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                style={{
                  padding: '7px 14px', fontSize: 13, fontWeight: 600,
                  background: 'var(--primary)', color: '#fff', border: 'none',
                  borderRadius: 'var(--radius)', cursor: 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Creating...' : 'Create Job'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
